/**
 * domain-sok.ts — innhold for /domener/sok («Søk etter domener»), norsk + engelsk
 *
 * ⚠ PLASSHOLDERE: tips, FAQ og tekster om .no-regler må leses gjennom før publisering.
 */

import type { Locale } from '@/types/landing';
import type { DomainSearchPageContent } from '@/types/domain';
import { DOMAIN_CONTACT } from './domain-shared';

const no: DomainSearchPageContent = {
  seoTitle: 'Søk etter domener — inDomain',
  breadcrumb: { label: 'Brødsmulesti', links: [{ label: 'Hjem', href: '/' }], current: 'Søk etter domener' },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'endelser', label: 'Populære endelser' },
      { id: 'tips', label: 'Slik velger du navn' },
      { id: 'hvorfor', label: 'Hvorfor inDomain' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se alle endelser', href: '/domener/endelser' },
  },
  hero: {
    heading: 'Søk etter domener',
    subheading: 'Finn og sjekk tilgjengeligheten av domenenavn.',
    primaryCta: { label: 'Se alle endelser', href: '/domener/endelser' },
    secondaryCta: { label: 'Slik velger du navn', href: '#tips' },
    illustrationLabel: 'Illustrasjon av et adressefelt, en klode og en prislapp',
  },
  popular: {
    eyebrow: 'Populære endelser',
    heading: 'Populære domeneendelser',
    intro: 'Velg endelsen som passer navnet og virksomheten din.',
    priceFrom: 'Fra',
    perYear: 'per år',
    footnote: '*Prisene er veiledende, per år og eksklusiv mva.',
  },
  tips: {
    eyebrow: 'Tips',
    heading: 'Slik velger du et godt domenenavn',
    intro: 'Fem ting å tenke på før du bestemmer deg.',
    panelLabel: 'Illustrasjon av et adressefelt og en klode',
    items: [
      { title: 'Ta med merkenavnet', body: 'Et navn folk kjenner igjen er lettere å huske og å finne.' },
      { title: 'Hold det kort', body: 'Korte navn er lettere å skrive riktig og å si høyt.' },
      { title: 'Gjør det lett å skrive', body: 'Unngå uvanlige stavemåter som folk må spørre om.' },
      { title: 'Unngå bindestrek og tall', body: 'De er lette å glemme eller forveksle når navnet leses opp.' },
      { title: 'Velg riktig endelse', body: '.no viser norsk tilhørighet, mens .com passer et internasjonalt publikum.' },
    ],
  },
  promo: {
    title: 'Har du domenet fra før?',
    body: 'Flytt det til inSuite og samle alt på ett sted.',
    cta: { label: 'Se hvordan', href: '/domener/flytt' },
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      { question: 'Hvordan sjekker jeg om et domene er ledig?', answer: 'Skriv navnet i søkefeltet. Vi sjekker ikke tilgjengelighet automatisk ennå, så trykk «Be om registrering», så sjekker vi domenet og registrerer det for deg.' },
      { question: 'Hvem kan registrere et .no-domene?', answer: 'For .no stiller registeret krav til tilknytning til Norge. Ta kontakt for detaljer om hva som gjelder for deg.' },
      { question: 'Kan jeg registrere flere endelser av samme navn?', answer: 'Ja. Mange registrerer både .no og .com for å beskytte navnet sitt.' },
      { question: 'Hva koster et domene?', answer: 'Prisen avhenger av endelsen. Se «Fullstendig TLD-liste» for priser per endelse.' },
    ],
  },
  cta: { heading: 'Funnet et navn?', body: 'Be om registrering, så ordner vi resten.', primary: { label: 'Kontakt oss', href: DOMAIN_CONTACT } },
};

const en: DomainSearchPageContent = {
  seoTitle: 'Search for domains — inDomain',
  breadcrumb: { label: 'Breadcrumb', links: [{ label: 'Home', href: '/' }], current: 'Search for domains' },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'endelser', label: 'Popular extensions' },
      { id: 'tips', label: 'Choosing a name' },
      { id: 'hvorfor', label: 'Why inDomain' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See all extensions', href: '/domener/endelser' },
  },
  hero: {
    heading: 'Search for domains',
    subheading: 'Find and check the availability of domain names.',
    primaryCta: { label: 'See all extensions', href: '/domener/endelser' },
    secondaryCta: { label: 'Choosing a name', href: '#tips' },
    illustrationLabel: 'Illustration of an address bar, a globe and a price tag',
  },
  popular: {
    eyebrow: 'Popular extensions',
    heading: 'Popular domain extensions',
    intro: 'Pick the extension that fits your name and your business.',
    priceFrom: 'From',
    perYear: 'per year',
    footnote: '*Prices are indicative, per year and exclude VAT.',
  },
  tips: {
    eyebrow: 'Tips',
    heading: 'How to choose a good domain name',
    intro: 'Five things to think about before you decide.',
    panelLabel: 'Illustration of an address bar and a globe',
    items: [
      { title: 'Include your brand name', body: 'A name people recognise is easier to remember and to find.' },
      { title: 'Keep it short', body: 'Short names are easier to type correctly and to say out loud.' },
      { title: 'Make it easy to spell', body: 'Avoid unusual spellings people have to ask about.' },
      { title: 'Avoid hyphens and numbers', body: 'They are easy to forget or mix up when the name is read aloud.' },
      { title: 'Pick the right extension', body: '.no signals a Norwegian connection, while .com suits an international audience.' },
    ],
  },
  promo: {
    title: 'Already have a domain?',
    body: 'Move it to inSuite and keep everything in one place.',
    cta: { label: 'See how', href: '/domener/flytt' },
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      { question: 'How do I check whether a domain is available?', answer: 'Enter the name in the search field. We do not check availability automatically yet, so press “Request registration”, and we will check the domain and register it for you.' },
      { question: 'Who can register a .no domain?', answer: 'For .no the registry sets requirements for a connection to Norway. Get in touch for details on what applies to you.' },
      { question: 'Can I register several extensions of the same name?', answer: 'Yes. Many register both .no and .com to protect their name.' },
      { question: 'How much does a domain cost?', answer: 'The price depends on the extension. See the “Full TLD list” for prices per extension.' },
    ],
  },
  cta: { heading: 'Found a name?', body: 'Request registration, and we will take care of the rest.', primary: { label: 'Contact us', href: DOMAIN_CONTACT } },
};

export const DOMAIN_SOK: Record<Locale, DomainSearchPageContent> = { no, en };
